import { useEffect } from 'react';
import type { ChoiceExercise } from '../../core/exercises.ts';
import { answerKey } from '../../core/answer.ts';
import { Icon } from '../../ui/Icon.tsx';
import { canSpeak, speak } from '../../ui/speech.ts';
import { PromptText } from './PromptText.tsx';

export function ChoiceView({
  exercise,
  selected,
  onChange,
  checked,
  speechLang,
}: {
  exercise: ChoiceExercise;
  selected: string[];
  onChange: (value: string[]) => void;
  checked: boolean;
  speechLang: string;
}) {
  const correct = new Set(exercise.correct.map(answerKey));
  const listen = exercise.listen;
  // Sentence options (listening, «что было дальше») go one per line.
  const long = exercise.options.some((o) => Array.from(o).length > 14);
  // A listening task plays its sentence once on open; the button repeats it.
  useEffect(() => {
    if (listen) speak(listen, speechLang);
  }, [listen, speechLang]);

  const toggle = (option: string) => {
    if (exercise.multi) {
      onChange(selected.includes(option) ? selected.filter((o) => o !== option) : [...selected, option]);
    } else {
      onChange([option]);
    }
  };
  return (
    <div className="stack">
      {listen ? (
        <button
          type="button"
          className="listen-btn"
          onClick={() => speak(listen, speechLang)}
          disabled={!canSpeak()}
          aria-label="Прослушать ещё раз"
        >
          <Icon name="speaker" size={30} />
          <span>{canSpeak() ? 'Послушать ещё раз' : 'Звук недоступен на этом устройстве'}</span>
        </button>
      ) : (
        exercise.prompt && <PromptText text={exercise.prompt} />
      )}
      {exercise.promptRu && <p className="t-meta">{exercise.promptRu}</p>}
      <div className="options" role={exercise.multi ? 'group' : 'radiogroup'} aria-label="Варианты ответа">
        {exercise.options.map((option) => {
          const isSelected = selected.includes(option);
          const state = checked
            ? correct.has(answerKey(option))
              ? ' option--correct'
              : isSelected
                ? ' option--wrong'
                : ''
            : isSelected
              ? ' option--selected'
              : '';
          return (
            <button
              key={option}
              type="button"
              role={exercise.multi ? 'checkbox' : 'radio'}
              aria-checked={isSelected}
              className={`option${long ? ' option--long' : ''}${state}`}
              disabled={checked}
              onClick={() => toggle(option)}
              lang={listen ? speechLang : undefined}
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}
